import {
	boolean,
	index,
	integer,
	jsonb,
	pgTable,
	text,
	timestamp,
	uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { branches } from "./branches";
import { productVariants } from "./catalog";
import { customers } from "./customers";
import { orders } from "./orders";

export const MEMBERSHIP_TIERS = ["NONE", "SILVER", "GOLD"] as const;
export type MembershipTier = (typeof MEMBERSHIP_TIERS)[number];

export const MEMBERSHIP_SOURCES = ["AUTO_SCAN", "ADMIN", "PURCHASE"] as const;
export type MembershipSource = (typeof MEMBERSHIP_SOURCES)[number];

export const MEMBERSHIP_BENEFIT_TYPES = [
	"MONTHLY_FREE_CUP",
	"BIRTHDAY_FREE_CUP",
	"NEW_LAUNCH_FREE",
	"TIER_DISCOUNT",
] as const;
export type MembershipBenefitType = (typeof MEMBERSHIP_BENEFIT_TYPES)[number];

export const MEMBERSHIP_QUOTA_RESET_MODES = [
	"CALENDAR_MONTH",
	"ROLLING_30_DAYS",
	"TIER_ANNIVERSARY",
] as const;
export type MembershipQuotaResetMode =
	(typeof MEMBERSHIP_QUOTA_RESET_MODES)[number];

export const MEMBERSHIP_RESERVATION_STATUSES = [
	"PENDING",
	"REDEEMED",
	"EXPIRED",
	"CANCELLED",
] as const;
export type MembershipReservationStatus =
	(typeof MEMBERSHIP_RESERVATION_STATUSES)[number];

export const customerMemberships = pgTable(
	"customer_memberships",
	{
		customerId: text("customer_id")
			.primaryKey()
			.references(() => customers.id, { onDelete: "cascade" }),
		tier: text("tier").$type<MembershipTier>().notNull().default("NONE"),
		source: text("source")
			.$type<MembershipSource>()
			.notNull()
			.default("AUTO_SCAN"),
		startedAt: timestamp("started_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
		upgradedAt: timestamp("upgraded_at", {
			withTimezone: true,
			mode: "date",
		}),
		createdAt: timestamp("created_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
		updatedAt: timestamp("updated_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
	},
	(t) => [index("customer_memberships_tier_idx").on(t.tier)],
);

export const membershipVisits = pgTable(
	"membership_visits",
	{
		id: text("id")
			.primaryKey()
			.$defaultFn(() => nanoid()),
		customerId: text("customer_id")
			.notNull()
			.references(() => customers.id, { onDelete: "cascade" }),
		orderId: text("order_id")
			.notNull()
			.unique()
			.references(() => orders.id, { onDelete: "cascade" }),
		branchId: text("branch_id").references(() => branches.id, {
			onDelete: "set null",
		}),
		visitedAt: timestamp("visited_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
	},
	(t) => [
		index("membership_visits_customer_visited_idx").on(
			t.customerId,
			t.visitedAt,
		),
	],
);

export const membershipEntitlements = pgTable(
	"membership_entitlements",
	{
		id: text("id")
			.primaryKey()
			.$defaultFn(() => nanoid()),
		customerId: text("customer_id")
			.notNull()
			.references(() => customers.id, { onDelete: "cascade" }),
		benefitType: text("benefit_type")
			.$type<MembershipBenefitType>()
			.notNull(),
		periodKey: text("period_key").notNull(),
		quotaTotal: integer("quota_total").notNull().default(0),
		quotaUsed: integer("quota_used").notNull().default(0),
		periodStartedAt: timestamp("period_started_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
		expiresAt: timestamp("expires_at", {
			withTimezone: true,
			mode: "date",
		}),
		metadata: jsonb("metadata").$type<Record<string, unknown> | null>(),
		createdAt: timestamp("created_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
		updatedAt: timestamp("updated_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
	},
	(t) => [
		uniqueIndex("membership_entitlements_customer_benefit_period_uidx").on(
			t.customerId,
			t.benefitType,
			t.periodKey,
		),
	],
);

export const membershipEntitlementReservations = pgTable(
	"membership_entitlement_reservations",
	{
		id: text("id")
			.primaryKey()
			.$defaultFn(() => nanoid()),
		entitlementId: text("entitlement_id")
			.notNull()
			.references(() => membershipEntitlements.id, { onDelete: "cascade" }),
		customerId: text("customer_id")
			.notNull()
			.references(() => customers.id, { onDelete: "cascade" }),
		variantId: text("variant_id").references(() => productVariants.id, {
			onDelete: "set null",
		}),
		token: text("token").notNull().unique(),
		status: text("status")
			.$type<MembershipReservationStatus>()
			.notNull()
			.default("PENDING"),
		expiresAt: timestamp("expires_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
		refOrderId: text("ref_order_id").references(() => orders.id, {
			onDelete: "set null",
		}),
		createdAt: timestamp("created_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
		updatedAt: timestamp("updated_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
	},
	(t) => [
		index("membership_reservations_customer_idx").on(t.customerId),
	],
);

export const membershipRedemptions = pgTable(
	"membership_redemptions",
	{
		id: text("id")
			.primaryKey()
			.$defaultFn(() => nanoid()),
		reservationId: text("reservation_id")
			.notNull()
			.references(() => membershipEntitlementReservations.id, {
				onDelete: "cascade",
			}),
		customerId: text("customer_id")
			.notNull()
			.references(() => customers.id, { onDelete: "cascade" }),
		benefitType: text("benefit_type")
			.$type<MembershipBenefitType>()
			.notNull(),
		variantId: text("variant_id").references(() => productVariants.id, {
			onDelete: "set null",
		}),
		orderId: text("order_id")
			.notNull()
			.references(() => orders.id, { onDelete: "cascade" }),
		branchId: text("branch_id").references(() => branches.id, {
			onDelete: "set null",
		}),
		redeemedAt: timestamp("redeemed_at", {
			withTimezone: true,
			mode: "date",
		}).notNull(),
	},
	(t) => [
		index("membership_redemptions_customer_idx").on(
			t.customerId,
			t.redeemedAt,
		),
	],
);

export const productLaunches = pgTable("product_launches", {
	id: text("id")
		.primaryKey()
		.$defaultFn(() => nanoid()),
	variantId: text("variant_id")
		.notNull()
		.unique()
		.references(() => productVariants.id, { onDelete: "cascade" }),
	launchAt: timestamp("launch_at", {
		withTimezone: true,
		mode: "date",
	}).notNull(),
	goldFreeEnabled: boolean("gold_free_enabled").notNull().default(true),
	notifySentAt: timestamp("notify_sent_at", {
		withTimezone: true,
		mode: "date",
	}),
	createdAt: timestamp("created_at", {
		withTimezone: true,
		mode: "date",
	}).notNull(),
	updatedAt: timestamp("updated_at", {
		withTimezone: true,
		mode: "date",
	}).notNull(),
});
